import { useEffect, useId, useRef, type ReactNode } from 'react';

export interface FilterSheetProps {
  open: boolean;
  /** Closes the sheet. Called by Apply, by Escape and by tapping the backdrop. */
  onClose: () => void;
  /** Resets the list's filters. Omit it and the sheet shows no Clear button. */
  onClear?: () => void;
  /** Heading and accessible name. Defaults to "Filters". */
  title?: string;
  /** The list's filter controls, stacked full width. */
  children: ReactNode;
}

/**
 * Phone bottom sheet for a list's filters (BR-MOB-002 / BR-MOB-003, #22193).
 *
 * Below 768px `DataListShell` hides its inline filter controls behind one "Filters (n)" button;
 * this sheet is where they go. The controls are stacked full width (fluid, no fixed widths) with
 * Clear and Apply. Filter controls in this codebase apply as they change, so Apply simply closes
 * the sheet; Clear calls the list's clear-all and keeps the sheet open so the reset is visible.
 *
 * Unlike the shared `Modal`, the sheet is a real `role="dialog"` with `aria-modal`, it moves focus
 * into itself on open, closes on Escape, and returns focus to whatever opened it.
 */
export function FilterSheet({ open, onClose, onClear, title = 'Filters', children }: FilterSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>('select, input, textarea, button');
    (first ?? panel)?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="md:hidden">
      <button
        type="button"
        aria-label="Close filters"
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default bg-gray-900/40"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] flex-col rounded-t-2xl bg-white shadow-2xl focus:outline-none"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <h2 id={titleId} className="flex-1 text-base font-semibold text-gray-900">{title}</h2>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="min-h-11 rounded-md px-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-md bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            Apply
          </button>
        </div>
        <div
          data-testid="filter-sheet-body"
          className="flex flex-col gap-3 overflow-y-auto p-4 [&_select]:min-h-11 [&_select]:w-full [&_select]:max-w-none [&_input]:w-full [&_input]:max-w-none [&>*]:w-full"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
